import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { toast } from 'sonner';
import { Bot, RefreshCw, Loader2, Clock, TrendingUp, Wallet, ClipboardCheck, AlertTriangle, Hourglass, ChevronDown, ChevronUp, Sparkles, ArrowRight, X } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TIP_META = {
  sure_asimi: { label: 'Süre Aşımı', icon: Clock },
  sure_yaklasan: { label: 'Bitiş Yaklaşıyor', icon: Hourglass },
  is_artisi: { label: 'İş Artışı', icon: TrendingUp },
  nakdi_asim: { label: 'Nakdi Aşım', icon: Wallet },
  kesin_kabul: { label: 'Kesin Kabul', icon: ClipboardCheck },
  eksiklik: { label: 'Kritik Eksiklik', icon: AlertTriangle },
  sure_uzatimi: { label: 'Süre Uzatımı', icon: Hourglass },
};

const SEVIYE = {
  kritik: { label: 'Kritik', border: 'border-l-red-500', bg: 'bg-red-900/15', text: 'text-red-300', badge: 'bg-red-600 text-white' },
  yuksek: { label: 'Yüksek', border: 'border-l-orange-500', bg: 'bg-orange-900/15', text: 'text-orange-300', badge: 'bg-orange-600 text-white' },
  orta: { label: 'Orta', border: 'border-l-amber-400', bg: 'bg-amber-900/10', text: 'text-amber-300', badge: 'bg-amber-500 text-gray-950' },
};

const UyariSatiri = ({ u, onGit }) => {
  const sev = SEVIYE[u.seviye] || SEVIYE.orta;
  const Icon = TIP_META[u.tip]?.icon || AlertTriangle;
  return (
    <div className={`rounded-lg border border-gray-800/60 border-l-4 ${sev.border} ${sev.bg} p-3`} data-testid={`uyari-${u.tip}-${u.ihale_id}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-start gap-2.5 min-w-0">
          <Icon className={`w-4 h-4 mt-0.5 flex-shrink-0 ${sev.text}`} />
          <div className="min-w-0">
            <p className="text-sm font-semibold text-white leading-tight">{u.baslik}</p>
            <p className="text-[11px] text-gray-400 mt-0.5 truncate">
              <span className="text-gray-200">{u.ihale_adi}</span>
              {u.ihale_no && u.ihale_no !== '-' && <span className="text-gray-500"> · {u.ihale_no}</span>}
              {u.ilce && <span className="text-gray-500"> · {u.ilce}</span>}
              <span className="text-gray-600"> · {u.kaynak === 'erp' ? 'İhaleler modülü' : 'ISY'}</span>
            </p>
            <p className="text-xs text-gray-300 mt-1.5 leading-relaxed">{u.aciklama}</p>
            {u.mevzuat && <p className="text-[10px] text-cyan-400/80 mt-1">Mevzuat: {u.mevzuat}</p>}
          </div>
        </div>
        <div className="flex flex-col items-end gap-1.5 flex-shrink-0">
          <Badge className={`text-[10px] ${sev.badge}`}>{sev.label}</Badge>
          <button onClick={() => onGit(u)} className="text-[10px] text-blue-300 hover:text-blue-200 inline-flex items-center gap-0.5" data-testid={`uyari-git-${u.tip}-${u.ihale_id}`}>
            İhaleye git <ArrowRight className="w-3 h-3" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default function ISYUyariPaneli({ onNavigate, refreshKey }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [seviyeFiltre, setSeviyeFiltre] = useState('tumu');
  const [tipFiltre, setTipFiltre] = useState('tumu');
  const [acik, setAcik] = useState(true);
  const [yorum, setYorum] = useState(null);
  const [yorumLoading, setYorumLoading] = useState(false);

  const fetchUyarilar = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/isy/asistan/uyarilar`, { withCredentials: true });
      setData(res.data);
    } catch (e) { toast.error('Uyarılar yüklenemedi'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchUyarilar(); }, [fetchUyarilar, refreshKey]);

  const aiYorum = async () => {
    setYorumLoading(true);
    try {
      const res = await axios.post(`${API}/isy/asistan/uyarilar/yorum`, {}, { withCredentials: true });
      setYorum(res.data.yorum);
    } catch (e) { toast.error(e.response?.data?.detail || 'AI yorumu alınamadı'); }
    finally { setYorumLoading(false); }
  };

  const git = (u) => {
    if (u.kaynak === 'erp' && !u.isy_id) window.open('/finance/ihaleler', '_blank');
    else onNavigate?.('ihaleler');
  };

  const uyarilar = (data?.uyarilar || []).filter(u => (seviyeFiltre === 'tumu' || u.seviye === seviyeFiltre) && (tipFiltre === 'tumu' || u.tip === tipFiltre));
  const ozet = data?.ozet || { kritik: 0, yuksek: 0, orta: 0, toplam: 0 };

  return (
    <Card className={`border ${ozet.kritik > 0 ? 'border-red-800/50' : ozet.toplam > 0 ? 'border-amber-800/40' : 'border-emerald-800/40'} bg-gray-900/80`} data-testid="asistan-uyari-paneli">
      <CardHeader className="pb-2">
        <CardTitle className="text-sm text-gray-300 flex items-center gap-2 flex-wrap">
          <Bot className="w-4 h-4 text-cyan-400" /> Asistan Uyarı Paneli
          <span className="text-[10px] text-gray-500 font-normal">
            {data ? `${data.taranan.erp_ihale + data.taranan.isy_ihale} ihale tarandı` : 'taranıyor…'}
          </span>
          <div className="ml-auto flex items-center gap-1.5">
            {[['kritik', 'Kritik'], ['yuksek', 'Yüksek'], ['orta', 'Orta']].map(([k, l]) => (
              <button key={k} onClick={() => setSeviyeFiltre(seviyeFiltre === k ? 'tumu' : k)}
                className={`text-[10px] px-2 py-0.5 rounded-full border transition-colors ${seviyeFiltre === k ? 'border-white/60 ring-1 ring-white/30' : 'border-transparent'} ${SEVIYE[k].badge} ${ozet[k] === 0 ? 'opacity-40' : ''}`}
                data-testid={`uyari-filtre-${k}`}>
                {ozet[k]} {l}
              </button>
            ))}
            <Button size="sm" variant="ghost" className="h-6 px-1.5 text-gray-400 hover:text-white" onClick={fetchUyarilar} disabled={loading} data-testid="uyari-yenile">
              <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
            </Button>
            <Button size="sm" variant="ghost" className="h-6 px-1.5 text-gray-400 hover:text-white" onClick={() => setAcik(!acik)} data-testid="uyari-daralt">
              {acik ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
            </Button>
          </div>
        </CardTitle>
      </CardHeader>
      {acik && (
        <CardContent className="pt-0">
          {data && ozet.toplam > 0 && (
            <div className="flex flex-wrap items-center gap-1.5 mb-3">
              <button onClick={() => setTipFiltre('tumu')} className={`text-[10px] px-2 py-0.5 rounded border ${tipFiltre === 'tumu' ? 'border-cyan-500 text-cyan-300 bg-cyan-900/20' : 'border-gray-700 text-gray-400 hover:text-gray-200'}`} data-testid="uyari-tip-tumu">Tümü ({ozet.toplam})</button>
              {Object.entries(data.tipler || {}).map(([tip, n]) => (
                <button key={tip} onClick={() => setTipFiltre(tipFiltre === tip ? 'tumu' : tip)}
                  className={`text-[10px] px-2 py-0.5 rounded border ${tipFiltre === tip ? 'border-cyan-500 text-cyan-300 bg-cyan-900/20' : 'border-gray-700 text-gray-400 hover:text-gray-200'}`} data-testid={`uyari-tip-${tip}`}>
                  {TIP_META[tip]?.label || tip} ({n})
                </button>
              ))}
              <Button size="sm" onClick={aiYorum} disabled={yorumLoading} className="ml-auto h-6 text-[11px] bg-cyan-700 hover:bg-cyan-600 text-white px-2" data-testid="uyari-ai-yorum">
                {yorumLoading ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Sparkles className="w-3 h-3 mr-1" />} AI Aksiyon Planı
              </Button>
            </div>
          )}
          {yorum && (
            <div className="mb-3 rounded-lg border border-cyan-800/40 bg-cyan-950/30 p-3 relative" data-testid="uyari-ai-yorum-sonuc">
              <button onClick={() => setYorum(null)} className="absolute top-2 right-2 text-gray-500 hover:text-gray-300"><X className="w-3.5 h-3.5" /></button>
              <div className="text-xs text-gray-200 leading-relaxed [&_p]:my-1 [&_ul]:my-1 [&_ul]:list-disc [&_ul]:pl-4 [&_ol]:my-1 [&_ol]:list-decimal [&_ol]:pl-4 [&_li]:my-0.5 [&_strong]:text-cyan-200 [&_h1]:text-sm [&_h2]:text-sm [&_h3]:text-xs [&_h1]:font-bold [&_h2]:font-bold [&_h3]:font-bold">
                <ReactMarkdown remarkPlugins={[remarkGfm]}>{yorum}</ReactMarkdown>
              </div>
            </div>
          )}
          {loading && !data ? (
            <div className="flex items-center gap-2 text-xs text-gray-500 py-4"><Loader2 className="w-4 h-4 animate-spin" /> İhaleler taranıyor…</div>
          ) : ozet.toplam === 0 ? (
            <p className="text-sm text-emerald-300/90 py-3 flex items-center gap-2" data-testid="uyari-yok"><ClipboardCheck className="w-4 h-4" /> Taranan ihalelerde süre aşımı veya iş artışı riski bulunmadı.</p>
          ) : uyarilar.length === 0 ? (
            <p className="text-xs text-gray-500 py-3">Bu filtreye uyan uyarı yok.</p>
          ) : (
            <div className="space-y-2 max-h-[420px] overflow-y-auto pr-1" data-testid="uyari-liste">
              {uyarilar.map(u => <UyariSatiri key={u.id} u={u} onGit={git} />)}
            </div>
          )}
          {data?.tarama_zamani && (
            <p className="text-[10px] text-gray-600 mt-2 text-right">Son tarama: {new Date(data.tarama_zamani).toLocaleString('tr-TR')} · kural tabanlı (API kredisi kullanmaz)</p>
          )}
        </CardContent>
      )}
    </Card>
  );
}
